import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, FileText, Calendar, MessageSquare, DollarSign, Palette, Image as ImageIcon, ShieldCheck, ArrowRight, Sparkles, Star, CheckCircle2 } from "lucide-react";
import { getMyProject } from "@/lib/portal/portal.functions";
import { getMyReviewLink } from "@/lib/reviews/reviews.functions";

export const Route = createFileRoute("/_authenticated/portal/dashboard")({
  component: DashboardPage,
});

const STAGE_LABELS: Record<string, string> = {
  inquiry: "Inquiry",
  estimate_scheduled: "Estimate Scheduled",
  estimate_completed: "Estimate Completed",
  proposal_sent: "Proposal Sent",
  proposal_approved: "Proposal Approved",
  deposit_received: "Deposit Received",
  materials_ordered: "Materials Ordered",
  project_scheduled: "Project Scheduled",
  preparation: "Preparation",
  painting: "Painting",
  cleanup: "Cleanup",
  final_walkthrough: "Final Walkthrough",
  completed: "Completed",
  warranty_active: "Warranty Active",
};

function DashboardPage() {
  const fn = useServerFn(getMyProject);
  const { data, isLoading } = useQuery({ queryKey: ["portal", "project"], queryFn: () => fn() });
  const reviewFn = useServerFn(getMyReviewLink);
  const { data: review } = useQuery({ queryKey: ["portal", "review-link"], queryFn: () => reviewFn(), staleTime: 60_000 });

  if (isLoading) {
    return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  }

  if (!data || !data.project) {
    return (
      <div className="py-20 grid place-items-center text-center">
        <div className="max-w-md">
          <Sparkles className="size-8 text-amber-300 mx-auto mb-3" />
          <h2 className="font-serif text-2xl text-white">No active project yet</h2>
          <p className="text-sm text-zinc-500 mt-2">Once your project manager invites you, your dashboard will light up with everything you need.</p>
        </div>
      </div>
    );
  }

  const project = data.project as any;
  const timeline = data.timeline as Array<any>;
  const completed = timeline.filter((t) => t.status === "complete").length;
  const total = Math.max(timeline.length, 14);
  const pct = Math.round((completed / total) * 100);

  const currentStage = timeline.find((t) => t.status === "in_progress") || timeline.find((t) => t.status !== "complete") || timeline[timeline.length - 1];
  const paid = (data.payments as Array<any>).filter((p) => p.status === "received").reduce((a, p) => a + Number(p.amount || 0), 0);
  const total$ = Number(project.total_cents ? project.total_cents / 100 : 0);
  const balance = Math.max(total$ - paid, 0);

  const next = data.nextAppointment as any;
  const nextDate = next ? new Date(next.starts_at) : null;

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-amber-300 text-[11px] tracking-[0.3em] uppercase font-semibold">Welcome back</p>
          <h1 className="font-serif text-4xl text-white mt-1">{project.customer_name?.split(" ")[0] ?? "There"}</h1>
          <p className="text-zinc-500 text-sm mt-1">{project.name}</p>
        </div>
        <Link to="/portal/messages" className="text-xs uppercase tracking-wider text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
          Message your PM <ArrowRight className="size-3" />
        </Link>
      </header>

      {/* Status / Progress card */}
      <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-amber-300/[0.06] via-white/[0.02] to-transparent p-8 overflow-hidden">
        <div className="absolute -top-20 -right-20 size-60 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />
        <div className="grid lg:grid-cols-3 gap-8 relative">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">Current status</p>
            <p className="font-serif text-2xl text-white mt-1">{STAGE_LABELS[currentStage?.stage] ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Stage {Math.min(completed + 1, total)} of {total}</p>
          </div>
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">Project progress</p>
              <p className="text-amber-300 text-sm font-semibold">{pct}%</p>
            </div>
            <div className="h-3 rounded-full bg-white/5 overflow-hidden border border-white/10">
              <div className="h-full bg-gradient-to-r from-amber-400 to-amber-200 transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-4 flex gap-1.5">
              {timeline.map((t) => (
                <div key={t.id} className={`flex-1 h-1 rounded-full ${t.status === "complete" ? "bg-amber-300" : t.status === "in_progress" ? "bg-amber-300/40" : "bg-white/5"}`} title={STAGE_LABELS[t.stage]} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Next appointment" value={nextDate ? nextDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "Not scheduled"}
          sub={nextDate ? nextDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"} Icon={Calendar} accent />
        <StatCard label="Project manager" value={project.project_manager_name || "Assigning…"} sub={project.project_manager_email || ""} Icon={Sparkles} />
        <StatCard label="Unread messages" value={String(data.unreadMessages)} sub={data.unreadMessages > 0 ? "Tap to view" : "All caught up"} Icon={MessageSquare} />
        <StatCard label="Outstanding balance" value={total$ > 0 ? `$${balance.toLocaleString()}` : "—"} sub={total$ > 0 ? `of $${total$.toLocaleString()} total` : "Awaiting proposal"} Icon={DollarSign} />
      </div>

      {/* Quick actions */}
      <section>
        <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-3">Quick Actions</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            { to: "/portal/proposal", label: "View Proposal", Icon: FileText },
            { to: "/portal/schedule", label: "View Schedule", Icon: Calendar },
            { to: "/portal/messages", label: "Message Painter", Icon: MessageSquare },
            { to: "/portal/gallery", label: "Upload Photos", Icon: ImageIcon },
            { to: "/portal/colors", label: "View Paint Colors", Icon: Palette },
            { to: "/portal/warranty", label: "View Warranty", Icon: ShieldCheck },
            { to: "/portal/documents", label: "Download Documents", Icon: FileText },
            { to: "/portal/payments", label: "Payments", Icon: DollarSign },
          ].map(({ to, label, Icon }) => (
            <Link key={to} to={to} className="group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-amber-300/30 p-4 transition-all">
              <Icon className="size-5 text-amber-300/70 group-hover:text-amber-300 transition" />
              <p className="text-sm text-white mt-3 font-medium">{label}</p>
              <ArrowRight className="size-3 text-zinc-400 group-hover:text-amber-300 absolute top-4 right-4 transition" />
            </Link>
          ))}
        </div>
      </section>

      {review && (
        review.submitted ? (
          <div className="rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] p-5 flex items-center gap-3">
            <CheckCircle2 className="size-5 text-amber-300" />
            <div className="flex-1">
              <p className="text-sm text-white font-semibold">Thank you for your {review.rating}★ review!</p>
              <p className="text-xs text-zinc-500">Submitted {review.submittedAt ? new Date(review.submittedAt).toLocaleDateString() : ""}</p>
            </div>
          </div>
        ) : (
          <a href={`/review/${review.token}`} target="_blank" rel="noopener noreferrer"
            className="block rounded-2xl border border-amber-300/30 bg-gradient-to-r from-amber-300/[0.06] to-transparent p-5 hover:border-amber-300/50 transition group">
            <div className="flex items-center gap-3">
              <Star className="size-5 text-amber-300" />
              <div className="flex-1">
                <p className="text-sm text-white font-semibold">Loved the work? Leave us a review.</p>
                <p className="text-xs text-zinc-500">Takes 30 seconds. Your feedback helps a local business.</p>
              </div>
              <ArrowRight className="size-4 text-zinc-500 group-hover:text-amber-300 transition" />
            </div>
          </a>
        )
      )}
    </div>
  );
}

function StatCard({ label, value, sub, Icon, accent }: { label: string; value: string; sub?: string; Icon: React.ComponentType<{ className?: string }>; accent?: boolean }) {
  return (
    <div className={`relative rounded-2xl border p-5 ${accent ? "border-amber-300/20 bg-amber-300/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
        <Icon className={`size-4 ${accent ? "text-amber-300" : "text-zinc-500"}`} />
      </div>
      <p className="mt-3 font-serif text-xl text-white leading-tight">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}